import { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

function Messages() {
  const [target, setTarget] = useState('all_drivers');
  const [userId, setUserId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [messageStatus, setMessageStatus] = useState(null); // { type: 'success' | 'error', text: string }
  
  // For searching specific users
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search for users/drivers
  useEffect(() => {
    if (target !== 'specific_user' || searchTerm.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        // Since we can't easily do full-text search in basic Firestore without an extension like Algolia,
        // we'll just fetch a few and filter client-side for this MVP, or search by exact email.
        // A simple approach is fetching the first 50 users and drivers and filtering locally.
        const usersSnap = await getDocs(query(collection(db, 'users'), limit(50)));
        const driversSnap = await getDocs(query(collection(db, 'drivers'), limit(50)));
        
        const allUsers = [
          ...usersSnap.docs.map(d => ({ id: d.id, role: 'Pasajero', ...d.data() })),
          ...driversSnap.docs.map(d => ({ id: d.id, role: 'Conductor', ...d.data() }))
        ];

        const term = searchTerm.toLowerCase();
        const filtered = allUsers.filter(u => 
          (u.email && u.email.toLowerCase().includes(term)) || 
          (u.name && u.name.toLowerCase().includes(term)) ||
          (u.firstName && u.firstName.toLowerCase().includes(term)) ||
          u.id.toLowerCase().includes(term)
        ).slice(0, 5);

        setSearchResults(filtered);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setIsSearching(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, target]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      setMessageStatus({ type: 'error', text: 'El título y el mensaje son obligatorios.' });
      return;
    }

    if (target === 'specific_user' && !userId) {
      setMessageStatus({ type: 'error', text: 'Debes seleccionar un usuario específico.' });
      return;
    }

    setIsSending(true);
    setMessageStatus(null);

    try {
      const functions = getFunctions();
      const sendAdminMessage = httpsCallable(functions, 'sendAdminMessage');
      
      const payload = { target, title, body };
      if (target === 'specific_user') {
        payload.userId = userId;
      }

      const result = await sendAdminMessage(payload);
      
      setMessageStatus({ type: 'success', text: result.data.message || 'Mensaje enviado con éxito.' });
      
      // Reset form on success
      setTitle('');
      setBody('');
      if (target === 'specific_user') {
        setSearchTerm('');
        setUserId('');
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessageStatus({ type: 'error', text: error.message || 'Error al enviar el mensaje.' });
    } finally {
      setIsSending(false);
    }
  };

  const handleSelectUser = (user) => {
    setUserId(user.id);
    setSearchTerm(user.email || user.name || user.firstName || user.id);
    setSearchResults([]);
  };

  return (
    <div className="animate-fade-in" style={{ paddingBottom: '2rem', position: 'relative', minHeight: '100vh', overflowX: 'clip' }}>
      <div className="page-header" style={{ marginBottom: '2rem', position: 'relative', zIndex: 1 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '2rem' }}>💬</span> Centro de Mensajería
          </h2>
          <p className="page-subtitle">Envía notificaciones push enriquecidas a los usuarios de DraiVen</p>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
        
        {/* === COMPOSE SECTION === */}
        <div className="card" style={{ 
          width: '100%',
          maxWidth: '900px',
          padding: '3rem', 
          position: 'relative', 
          overflow: 'visible', // allow dropdowns to overflow
          background: 'rgba(20, 20, 20, 0.6)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 215, 0, 0.15)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
          borderRadius: '32px'
        }}>
          {/* Subtle background glow */}
          <div style={{ position: 'absolute', top: '0', right: '0', width: '100%', height: '100%', background: 'radial-gradient(circle at top right, rgba(255, 215, 0, 0.08) 0%, transparent 60%)', pointerEvents: 'none', borderRadius: '32px' }} />
          
          <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem', position: 'relative', zIndex: 1 }}>
            
            {/* Target Audience Selection */}
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '1.2rem', marginBottom: '1rem', color: '#FFD700', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ background: '#FFD700', color: '#000', width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontSize: '0.9rem', fontWeight: 'bold' }}>1</span>
                ¿A quién quieres llegar?
              </label>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                {[
                  { id: 'all_drivers', icon: '🏍️', label: 'Todos los Conductores', desc: 'Avisos y actualizaciones' },
                  { id: 'all_passengers', icon: '👥', label: 'Todos los Pasajeros', desc: 'Promociones masivas' },
                  { id: 'specific_user', icon: '🎯', label: 'Usuario Específico', desc: 'Mensaje directo' }
                ].map(opt => (
                  <div 
                    key={opt.id}
                    onClick={() => {
                      setTarget(opt.id);
                      setUserId('');
                      setSearchTerm('');
                    }}
                    style={{
                      background: target === opt.id ? 'rgba(255, 215, 0, 0.1)' : 'rgba(0,0,0,0.2)',
                      border: `2px solid ${target === opt.id ? '#FFD700' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '20px',
                      padding: '1.5rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      textAlign: 'center',
                      boxShadow: target === opt.id ? '0 8px 25px rgba(255, 215, 0, 0.15)' : 'none',
                      transform: target === opt.id ? 'translateY(-4px)' : 'none'
                    }}
                    onMouseOver={(e) => { if(target !== opt.id) e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.4)' }}
                    onMouseOut={(e) => { if(target !== opt.id) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)' }}
                  >
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem', filter: target === opt.id ? 'drop-shadow(0 0 10px rgba(255,215,0,0.5))' : 'grayscale(0.5)' }}>{opt.icon}</div>
                    <div style={{ fontWeight: 600, color: target === opt.id ? '#FFD700' : '#fff', marginBottom: '6px', fontSize: '1.1rem' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Specific User Search */}
            {target === 'specific_user' && (
              <div className="form-group animate-fade-in" style={{ position: 'relative', background: 'rgba(0,0,0,0.2)', padding: '2rem', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <label className="form-label" htmlFor="userSearch" style={{ color: '#fff', fontSize: '1.1rem' }}>Buscar Destinatario</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="userSearch"
                    type="text"
                    className="form-control"
                    placeholder="Escribe un email, nombre o ID..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (userId) setUserId('');
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      paddingLeft: '50px',
                      height: '60px',
                      fontSize: '1.1rem',
                      borderRadius: '16px',
                      color: '#fff'
                    }}
                  />
                  <span style={{ position: 'absolute', left: '18px', top: '16px', fontSize: '1.2rem', opacity: 0.5 }}>🔍</span>
                  
                  {isSearching && (
                    <div style={{ position: 'absolute', right: '18px', top: '20px' }}>
                      <div className="spinner" style={{ width: '20px', height: '20px', borderTopColor: '#FFD700' }} />
                    </div>
                  )}
                </div>

                {/* Dropdown Results */}
                {searchResults.length > 0 && !userId && (
                  <div style={{
                    position: 'absolute', 
                    top: 'calc(100% - 10px)', 
                    left: '2rem', 
                    right: '2rem', 
                    background: '#1a1a1a', 
                    border: '1px solid rgba(255,215,0,0.3)', 
                    borderRadius: '16px', 
                    marginTop: '8px',
                    zIndex: 20,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    boxShadow: '0 15px 40px rgba(0,0,0,0.6)'
                  }}>
                    {searchResults.map((user, idx) => (
                      <div 
                        key={user.id} 
                        onClick={() => handleSelectUser(user)}
                        style={{
                          padding: '16px 20px', 
                          cursor: 'pointer', 
                          borderBottom: idx < searchResults.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,215,0,0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <img 
                            src={user.photoUrl || `https://ui-avatars.com/api/?name=${user.name || user.firstName || 'U'}&background=FFD700&color=000`} 
                            alt="avatar" 
                            style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }}
                          />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#fff' }}>{user.name || user.firstName || 'Sin Nombre'}</div>
                            <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>{user.email || user.phone || user.id}</div>
                          </div>
                        </div>
                        <span style={{ 
                          fontSize: '0.85rem', 
                          padding: '6px 12px', 
                          borderRadius: '12px', 
                          background: user.role === 'Conductor' ? 'rgba(249, 115, 22, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                          color: user.role === 'Conductor' ? '#f97316' : '#10b981',
                          fontWeight: 'bold'
                        }}>
                          {user.role}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)', margin: '1rem 0' }} />

            {/* Compose Area */}
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '1.2rem', marginBottom: '1rem', color: '#FFD700', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ background: '#FFD700', color: '#000', width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontSize: '0.9rem', fontWeight: 'bold' }}>2</span>
                Diseña el Mensaje
              </label>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', background: 'rgba(0,0,0,0.2)', padding: '2.5rem', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>
                  <label className="form-label" htmlFor="msgTitle" style={{ color: '#fff', fontSize: '1.05rem', marginBottom: '12px' }}>Título Principal <span style={{color: '#FFD700'}}>*</span></label>
                  <input
                    id="msgTitle"
                    type="text"
                    className="form-control"
                    placeholder="Ej. ¡Aprovecha nuestra nueva promo!"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    maxLength={50}
                    style={{ 
                      width: '100%',
                      boxSizing: 'border-box',
                      background: 'rgba(0,0,0,0.4)', 
                      border: '2px solid rgba(255,255,255,0.1)', 
                      height: '64px', 
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      borderRadius: '16px',
                      padding: '0 20px',
                      color: '#fff'
                    }}
                  />
                </div>

                <div>
                  <label className="form-label" htmlFor="msgBody" style={{ color: '#fff', fontSize: '1.05rem', marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Contenido del Mensaje <span style={{color: '#FFD700'}}>*</span></span>
                    <span style={{ color: body.length >= 250 ? '#ef4444' : 'rgba(255,255,255,0.4)' }}>{body.length}/250</span>
                  </label>
                  <textarea
                    id="msgBody"
                    className="form-control"
                    placeholder="Escribe el contenido detallado de la notificación aquí..."
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    required
                    maxLength={250}
                    rows={5}
                    style={{ 
                      width: '100%',
                      boxSizing: 'border-box',
                      resize: 'vertical', 
                      background: 'rgba(0,0,0,0.4)', 
                      border: '2px solid rgba(255,255,255,0.1)', 
                      lineHeight: '1.6',
                      fontSize: '1.15rem',
                      borderRadius: '16px',
                      padding: '20px',
                      color: '#fff'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Status Feedback */}
            {messageStatus && (
              <div className="animate-fade-in" style={{
                background: messageStatus.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: `2px solid ${messageStatus.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                padding: '1.25rem',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                color: messageStatus.type === 'success' ? '#10b981' : '#ef4444',
                fontSize: '1.1rem',
                fontWeight: 600
              }}>
                <span style={{ fontSize: '1.5rem' }}>{messageStatus.type === 'success' ? '✅' : '⚠️'}</span>
                {messageStatus.text}
              </div>
            )}

            {/* Submit */}
            <button 
              type="submit" 
              className="btn"
              disabled={isSending || (target === 'specific_user' && !userId) || !title || !body}
              style={{ 
                marginTop: '1rem', 
                height: '70px', 
                fontSize: '1.25rem', 
                fontWeight: 'bold',
                background: (isSending || (target === 'specific_user' && !userId) || !title || !body) ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #FFD700 0%, #F59E0B 100%)',
                color: (isSending || (target === 'specific_user' && !userId) || !title || !body) ? 'rgba(255,255,255,0.3)' : '#000',
                border: 'none',
                borderRadius: '20px',
                boxShadow: (isSending || (target === 'specific_user' && !userId) || !title || !body) ? 'none' : '0 10px 25px rgba(255, 215, 0, 0.4)',
                cursor: (isSending || (target === 'specific_user' && !userId) || !title || !body) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {isSending ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '16px', justifyContent: 'center' }}>
                  <span className="spinner" style={{ width: '24px', height: '24px', borderTopColor: '#000' }} />
                  Enviando Notificación...
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                  🚀 Difundir a Usuarios
                </span>
              )}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}

export default Messages;
